import { useCallback, useMemo, useRef, useState } from 'react'
import type {
	DictationFieldProfile,
	DictationReviewSnapshot,
	DictationSessionToken,
	DictationState,
	DictationTone,
} from '../components/dictation/dictation.types'
import {
	appendTranscriptValue,
	applyFormattingProfile,
	normalizeTranscript,
} from '../components/dictation/dictation.utils'

interface SpeechRecognitionAlternative {
	transcript: string
	confidence: number
}

interface SpeechRecognitionResultLike {
	isFinal: boolean
	0: SpeechRecognitionAlternative
}

interface SpeechRecognitionEventLike {
	resultIndex: number
	results: SpeechRecognitionResultLike[]
}

interface SpeechRecognitionInstance {
	continuous: boolean
	interimResults: boolean
	lang: string
	onstart: (() => void) | null
	onresult: ((event: SpeechRecognitionEventLike) => void) | null
	onerror: ((event: { error?: string }) => void) | null
	onend: (() => void) | null
	start: () => void
	stop: () => void
}

type SpeechRecognitionConstructor = new () => SpeechRecognitionInstance

interface BrowserSpeechWindow extends Window {
	SpeechRecognition?: SpeechRecognitionConstructor
	webkitSpeechRecognition?: SpeechRecognitionConstructor
}

type DictationProvider = 'browser' | 'wisprflow'

export interface UseDictationOptions {
	fieldId: string
	value: string
	profile: DictationFieldProfile
	onChangeValue: (next: string) => void
	onRequestSessionToken?: () => Promise<DictationSessionToken | null>
	onSnapshot?: (snapshot: DictationReviewSnapshot) => void
	onRewrite?: (input: string, tone: DictationTone) => Promise<string>
}

export interface UseDictationResult {
	state: DictationState
	isListening: boolean
	interimTranscript: string
	draftTranscript: string
	error: string | null
	canUndoRewrite: boolean
	startDictation: () => Promise<void>
	stopDictation: () => void
	insertDraft: () => void
	replaceWithDraft: () => void
	discardDraft: () => void
	resetDraft: () => void
	rewriteText: (tone: DictationTone) => Promise<void>
	undoRewrite: () => void
}

const MAX_AUTO_RESTARTS = 50
const WISPR_SAMPLE_RATE = 16_000

function downsampleTo16k(input: Float32Array, sourceSampleRate: number): Float32Array {
	if (sourceSampleRate === WISPR_SAMPLE_RATE) {
		return input
	}
	const ratio = sourceSampleRate / WISPR_SAMPLE_RATE
	const length = Math.max(1, Math.round(input.length / ratio))
	const output = new Float32Array(length)
	for (let i = 0; i < length; i += 1) {
		const start = Math.floor(i * ratio)
		const end = Math.min(input.length, Math.floor((i + 1) * ratio))
		let sum = 0
		let count = 0
		for (let j = start; j < end; j += 1) {
			sum += input[j] ?? 0
			count += 1
		}
		output[i] = count > 0 ? sum / count : 0
	}
	return output
}

function convertFloat32ToInt16(input: Float32Array): Int16Array {
	const output = new Int16Array(input.length)
	for (let i = 0; i < input.length; i += 1) {
		const sample = Math.max(-1, Math.min(1, input[i] ?? 0))
		output[i] = sample < 0 ? Math.round(sample * 32768) : Math.round(sample * 32767)
	}
	return output
}

function int16ToBase64(input: Int16Array): string {
	const bytes = new Uint8Array(input.buffer)
	const CHUNK_SIZE = 0x8000
	let binary = ''
	for (let i = 0; i < bytes.length; i += CHUNK_SIZE) {
		const chunk = bytes.subarray(i, i + CHUNK_SIZE)
		binary += String.fromCharCode(...chunk)
	}
	return btoa(binary)
}

export function useDictation({
	fieldId,
	value,
	profile,
	onChangeValue,
	onRequestSessionToken,
	onSnapshot,
	onRewrite,
}: UseDictationOptions): UseDictationResult {
	const recognitionRef = useRef<SpeechRecognitionInstance | null>(null)
	const providerRef = useRef<DictationProvider>('browser')
	const stoppedByUserRef = useRef(false)
	const autoRestartCountRef = useRef(0)
	const wisprSocketRef = useRef<WebSocket | null>(null)
	const audioContextRef = useRef<AudioContext | null>(null)
	const mediaStreamRef = useRef<MediaStream | null>(null)
	const processorRef = useRef<ScriptProcessorNode | null>(null)
	const mediaSourceRef = useRef<MediaStreamAudioSourceNode | null>(null)
	const packetPositionRef = useRef(0)
	const wisprFinalizeTimerRef = useRef<number | null>(null)
	const browserPreviewRecognitionRef = useRef<SpeechRecognitionInstance | null>(null)
	const browserPreviewFinalTextRef = useRef('')
	const [state, setState] = useState<DictationState>('idle')
	const [error, setError] = useState<string | null>(null)
	const [interimTranscript, setInterimTranscript] = useState('')
	const [draftTranscript, setDraftTranscript] = useState('')
	const [preRewriteText, setPreRewriteText] = useState<string | null>(null)

	const buildRecognition = useCallback(() => {
		const speechWindow = window as BrowserSpeechWindow
		const Ctor = speechWindow.SpeechRecognition ?? speechWindow.webkitSpeechRecognition
		if (!Ctor) {
			throw new Error('Browser speech recognition is not available')
		}
		const recognition = new Ctor()
		recognition.continuous = true
		recognition.interimResults = true
		recognition.lang = 'en-US'
		return recognition
	}, [])

	const appendDraft = useCallback((chunk: string) => {
		const normalized = normalizeTranscript(chunk)
		if (!normalized) return
		setDraftTranscript((prev) => (prev ? `${prev} ${normalized}`.trim() : normalized))
		setPreRewriteText(null)
	}, [])

	const stopBrowserPreview = useCallback(() => {
		browserPreviewRecognitionRef.current?.stop()
		browserPreviewRecognitionRef.current = null
		browserPreviewFinalTextRef.current = ''
	}, [])

	const startBrowserPreview = useCallback(() => {
		try {
			const recognition = buildRecognition()
			browserPreviewRecognitionRef.current = recognition

			recognition.onresult = (event) => {
				let interim = ''
				for (let i = event.resultIndex; i < event.results.length; i += 1) {
					const result = event.results[i]
					const alternative = result[0]
					if (!alternative?.transcript) continue
					if (result.isFinal) {
						const nextFinal = normalizeTranscript(
							[browserPreviewFinalTextRef.current, alternative.transcript].join(' '),
						)
						browserPreviewFinalTextRef.current = nextFinal
					} else {
						interim = alternative.transcript
					}
				}
				setInterimTranscript(normalizeTranscript([browserPreviewFinalTextRef.current, interim].join(' ')))
			}

			recognition.onerror = () => {
				// Live preview is best-effort only.
			}

			recognition.onend = () => {
				if (stoppedByUserRef.current || providerRef.current !== 'wisprflow') {
					return
				}
				try {
					const next = buildRecognition()
					browserPreviewRecognitionRef.current = next
					next.onresult = recognition.onresult
					next.onerror = recognition.onerror
					next.onend = recognition.onend
					next.start()
				} catch {
					// Ignore preview restart failures.
				}
			}

			recognition.start()
		} catch {
			// Browser preview unavailable; Wispr partial/final still works.
		}
	}, [buildRecognition])

	const detachWisprAudio = useCallback(() => {
		processorRef.current?.disconnect()
		mediaSourceRef.current?.disconnect()
		processorRef.current = null
		mediaSourceRef.current = null

		mediaStreamRef.current?.getTracks().forEach((track) => {
			track.stop()
		})
		mediaStreamRef.current = null

		if (audioContextRef.current) {
			void audioContextRef.current.close()
			audioContextRef.current = null
		}
	}, [])

	const clearWisprFinalizeTimer = useCallback(() => {
		if (wisprFinalizeTimerRef.current !== null) {
			window.clearTimeout(wisprFinalizeTimerRef.current)
			wisprFinalizeTimerRef.current = null
		}
	}, [])

	const stopWispr = useCallback(
		(awaitFinalResult: boolean) => {
			const ws = wisprSocketRef.current
			const totalPackets = packetPositionRef.current
			detachWisprAudio()
			clearWisprFinalizeTimer()

			if (!ws) {
				packetPositionRef.current = 0
				return
			}

			if (awaitFinalResult && ws.readyState === WebSocket.OPEN && totalPackets > 0) {
				ws.send(
					JSON.stringify({
						type: 'commit',
						total_packets: totalPackets,
					}),
				)
				wisprFinalizeTimerRef.current = window.setTimeout(() => {
					if (ws.readyState === WebSocket.OPEN) {
						ws.close()
					}
				}, 8_000)
				return
			}

			if (ws.readyState === WebSocket.OPEN || ws.readyState === WebSocket.CONNECTING) {
				ws.close()
			}
			wisprSocketRef.current = null
			packetPositionRef.current = 0
		},
		[clearWisprFinalizeTimer, detachWisprAudio],
	)

	const startWispr = useCallback(
		async (sessionToken: string) => {
			const stream = await navigator.mediaDevices.getUserMedia({ audio: true })
			mediaStreamRef.current = stream

			const audioContext = new AudioContext()
			audioContextRef.current = audioContext
			await audioContext.resume()

			const mediaSource = audioContext.createMediaStreamSource(stream)
			mediaSourceRef.current = mediaSource

			const processor = audioContext.createScriptProcessor(4096, 1, 1)
			processorRef.current = processor

			const ws = new WebSocket(
				`wss://platform-api.wisprflow.ai/api/v1/dash/client_ws?client_key=${encodeURIComponent(`Bearer ${sessionToken}`)}`,
			)
			wisprSocketRef.current = ws
			packetPositionRef.current = 0

			await new Promise<void>((resolve, reject) => {
				const timeout = window.setTimeout(() => reject(new Error('Timed out connecting to Wispr')), 10_000)
				ws.onopen = () => {
					window.clearTimeout(timeout)
					resolve()
				}
				ws.onerror = () => {
					window.clearTimeout(timeout)
					reject(new Error('Unable to connect to Wispr dictation service'))
				}
			})

			ws.onmessage = (event) => {
				try {
					const payload = JSON.parse(event.data) as {
						status?: string
						body?: { text?: string }
						final?: boolean
						error?: string
					}
					if (payload.status === 'text' && typeof payload.body?.text === 'string') {
						const normalizedText = normalizeTranscript(payload.body.text)
						if (payload.final) {
							setDraftTranscript(normalizedText)
							setInterimTranscript('')
							setPreRewriteText(null)
							stopBrowserPreview()
							clearWisprFinalizeTimer()
							ws.close()
						} else if (!browserPreviewRecognitionRef.current) {
							// Use Wispr partials only when browser preview is unavailable.
							setInterimTranscript(normalizedText)
						}
					} else if (payload.status === 'error') {
						setState('error')
						setError(payload.error || 'Wispr dictation failed')
					}
				} catch {
					// Ignore malformed frames and keep stream alive.
				}
			}

			ws.onclose = () => {
				clearWisprFinalizeTimer()
				if (wisprSocketRef.current === ws) {
					wisprSocketRef.current = null
					packetPositionRef.current = 0
				}
				if (!stoppedByUserRef.current) {
					setState('idle')
				}
			}

			ws.send(
				JSON.stringify({
					type: 'auth',
					access_token: sessionToken,
					language: ['en'],
					context: {
						app: { type: 'other' },
						textbox_contents: {
							before_text: value,
							selected_text: '',
							after_text: '',
						},
						dictionary_context: [],
					},
				}),
			)

			processor.onaudioprocess = (event) => {
				const currentWs = wisprSocketRef.current
				if (!currentWs || currentWs.readyState !== WebSocket.OPEN) {
					return
				}
				const input = event.inputBuffer.getChannelData(0)
				const downsampled = downsampleTo16k(input, audioContext.sampleRate)
				const int16 = convertFloat32ToInt16(downsampled)
				const base64Packet = int16ToBase64(int16)
				const rms = Math.sqrt(
					downsampled.reduce((sum, sample) => sum + sample * sample, 0) / Math.max(1, downsampled.length),
				)

				currentWs.send(
					JSON.stringify({
						type: 'append',
						position: packetPositionRef.current,
						audio_packets: {
							packets: [base64Packet],
							volumes: [rms],
							packet_duration: downsampled.length / WISPR_SAMPLE_RATE,
							audio_encoding: 'wav',
							byte_encoding: 'base64',
						},
					}),
				)
				packetPositionRef.current += 1
			}

			mediaSource.connect(processor)
			processor.connect(audioContext.destination)
			startBrowserPreview()
			setState('listening')
		},
		[clearWisprFinalizeTimer, startBrowserPreview, stopBrowserPreview, value],
	)

	const wireRecognition = useCallback(
		(recognition: SpeechRecognitionInstance) => {
			recognition.onstart = () => {
				setState('listening')
			}

			recognition.onresult = (event) => {
				let interim = ''
				for (let i = event.resultIndex; i < event.results.length; i += 1) {
					const result = event.results[i]
					const alternative = result[0]
					if (!alternative?.transcript) continue
					if (result.isFinal) {
						appendDraft(alternative.transcript)
					} else {
						interim = alternative.transcript
					}
				}
				setInterimTranscript(interim)
			}

			recognition.onerror = (event) => {
				if (event.error === 'aborted') return
				setState('error')
				setError(event.error || 'Unable to process dictation')
			}

			recognition.onend = () => {
				setInterimTranscript('')
				if (stoppedByUserRef.current) {
					setState('idle')
					return
				}
				if (autoRestartCountRef.current >= MAX_AUTO_RESTARTS) {
					setState('idle')
					return
				}
				try {
					autoRestartCountRef.current += 1
					const next = buildRecognition()
					recognitionRef.current = next
					wireRecognition(next)
					next.start()
				} catch {
					setState('idle')
				}
			}
		},
		[appendDraft, buildRecognition],
	)

	const startDictation = useCallback(async () => {
		try {
			setError(null)
			stoppedByUserRef.current = false
			autoRestartCountRef.current = 0
			setState('requesting_permission')

			let session: DictationSessionToken | null = null
			if (onRequestSessionToken) {
				try {
					session = await onRequestSessionToken()
				} catch (tokenError) {
					console.error('Dictation token request failed, falling back to browser recognition:', tokenError)
				}
			}

			if (session?.provider === 'wisprflow' && session.token) {
				try {
					providerRef.current = 'wisprflow'
					await startWispr(session.token)
					return
				} catch (wisprError) {
					console.error('Wispr start failed, falling back to browser recognition:', wisprError)
					stopWispr(false)
				}
			}

			providerRef.current = 'browser'
			const recognition = buildRecognition()
			recognitionRef.current = recognition
			wireRecognition(recognition)
			recognition.start()
		} catch (err) {
			setState('error')
			setError(err instanceof Error ? err.message : 'Unable to start dictation')
		}
	}, [buildRecognition, onRequestSessionToken, startWispr, stopWispr, wireRecognition])

	const stopDictation = useCallback(() => {
		stoppedByUserRef.current = true
		if (providerRef.current === 'wisprflow') {
			stopWispr(true)
			stopBrowserPreview()
			setState('idle')
		} else {
			recognitionRef.current?.stop()
			recognitionRef.current = null
		}
		setInterimTranscript('')
	}, [stopBrowserPreview, stopWispr])

	const clearDraftState = useCallback(() => {
		setDraftTranscript('')
		setInterimTranscript('')
		setPreRewriteText(null)
	}, [])

	const insertDraft = useCallback(() => {
		const normalized = normalizeTranscript([draftTranscript, interimTranscript].join(' '))
		if (!normalized) return
		const nextValue = appendTranscriptValue(value, normalized, profile)
		onChangeValue(nextValue)
		onSnapshot?.({
			fieldId,
			before: value,
			after: nextValue,
			lastUpdatedAt: new Date().toISOString(),
		})
		clearDraftState()
	}, [clearDraftState, draftTranscript, fieldId, interimTranscript, onChangeValue, onSnapshot, profile, value])

	const replaceWithDraft = useCallback(() => {
		const normalized = normalizeTranscript([draftTranscript, interimTranscript].join(' '))
		if (!normalized) return
		const nextValue = applyFormattingProfile(normalized, profile)
		onChangeValue(nextValue)
		onSnapshot?.({
			fieldId,
			before: value,
			after: nextValue,
			lastUpdatedAt: new Date().toISOString(),
		})
		clearDraftState()
	}, [clearDraftState, draftTranscript, fieldId, interimTranscript, onChangeValue, onSnapshot, profile, value])

	const discardDraft = useCallback(() => {
		clearDraftState()
	}, [clearDraftState])

	const resetDraft = useCallback(() => {
		stoppedByUserRef.current = true
		if (providerRef.current === 'wisprflow') {
			stopWispr(false)
			stopBrowserPreview()
		} else {
			recognitionRef.current?.stop()
			recognitionRef.current = null
		}
		clearDraftState()
		setError(null)
		setState('idle')
	}, [clearDraftState, stopBrowserPreview, stopWispr])

	const rewriteText = useCallback(
		async (tone: DictationTone) => {
			if (!onRewrite) return
			setState('processing')
			setError(null)
			try {
				const sourceText = draftTranscript.trim() || value
				const nextValue = await onRewrite(sourceText, tone)
				if (draftTranscript.trim()) {
					setPreRewriteText(draftTranscript)
					setDraftTranscript(nextValue)
				} else {
					setPreRewriteText(value)
					onChangeValue(nextValue)
					onSnapshot?.({
						fieldId,
						before: value,
						after: nextValue,
						lastUpdatedAt: new Date().toISOString(),
					})
				}
				setState('idle')
			} catch (err) {
				setState('error')
				setError(err instanceof Error ? err.message : 'Unable to rewrite dictated text')
			}
		},
		[draftTranscript, fieldId, onChangeValue, onRewrite, onSnapshot, value],
	)

	const undoRewrite = useCallback(() => {
		if (preRewriteText === null) return
		if (draftTranscript.trim()) {
			setDraftTranscript(preRewriteText)
		} else {
			onChangeValue(preRewriteText)
		}
		setPreRewriteText(null)
	}, [draftTranscript, onChangeValue, preRewriteText])

	const isListening = useMemo(() => state === 'listening', [state])
	const canUndoRewrite = preRewriteText !== null

	return {
		state,
		isListening,
		interimTranscript,
		draftTranscript,
		error,
		canUndoRewrite,
		startDictation,
		stopDictation,
		insertDraft,
		replaceWithDraft,
		discardDraft,
		resetDraft,
		rewriteText,
		undoRewrite,
	}
}
